import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Lock, Unlock, Pencil, ArrowLeft, Wallet, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useLoad, act } from "@/lib/hooks";
import { api, fmt, fdate, fdatetime } from "@/lib/api";
import { useAccess } from "@/context/AuthContext";
import { downloadWith } from "@/lib/portalApi";
import { SendDocButton } from "@/components/SendDocButton";
import { PageHeader, StatusBadge, StatCard, Section, EmptyRow } from "@/components/common";
import { RaiseTicketButton } from "@/components/agyin/RaiseTicketButton";
import { MilestoneSchedule } from "@/components/MilestoneSchedule";
import { TaxTotals } from "@/components/TaxChips";
import { InvoiceForm } from "./InvoiceForm";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import { CancelInvoiceButton, CancelledBanner } from "./CancelInvoice";
import { InvoiceVersions, UpdateFromQuoteButton } from "./InvoiceVersions";
import { JobOrderPanel } from "./JobOrder";

function AuditTrail({ rows }) {
  return (
    <ul className="divide-y divide-slate-100" data-testid="invoice-audit-trail">
      {rows.length === 0 && <li className="px-5 py-6 text-sm text-slate-400">No audit entries.</li>}
      {rows.map((a) => (
        <li key={a.id} className="px-5 py-3 text-sm">
          <div className="flex justify-between gap-3"><span className="font-medium capitalize text-slate-900">{a.action.replace(/_/g, " ")}</span><span className="text-xs text-slate-400">{fdatetime(a.at)}</span></div>
          <div className="text-xs text-slate-500">by {a.user_name}{a.reason && <> · reason: <span className="text-slate-700">{a.reason}</span></>}</div>
          {a.before?.total !== undefined && a.after?.total !== undefined && <div className="text-xs text-slate-500">Total {fmt(a.before.total)} → {fmt(a.after.total)}</div>}
        </li>
      ))}
    </ul>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const [d, reload] = useLoad(`/tenant/invoices/${id}`);
  const [edit, setEdit] = useState(false);
  const [unlock, setUnlock] = useState(false);
  const [pay, setPay] = useState(false);
  const [reason, setReason] = useState("");
  const { canWrite } = useAccess();
  if (!d) return null;
  const inv = d.invoice;
  const cancelled = inv.status === "cancelled";
  const bal = cancelled ? 0 : Math.round((inv.total - inv.amount_paid - (inv.amount_credited || 0)) * 100) / 100;

  const doUnlock = async () => {
    if (await act(() => api.post(`/tenant/invoices/${id}/unlock`, { reason }), "Invoice unlocked for editing")) { setUnlock(false); setReason(""); reload(); }
  };
  const claim = inv.payment_claim;
  const resolveClaim = async (kind) => {
    const note = kind === "dismiss" ? window.prompt("Reason for dismissing (shown to the customer)") : null;
    if (kind === "dismiss" && note === null) return;
    if (await act(() => api.post(`/tenant/invoices/${id}/payment-claim/${kind}`, { note }), kind === "confirm" ? "Claim confirmed. Record the payment to update the balance." : "Claim dismissed")) reload();
  };

  return (
    <>
      <Link to="/app/invoices" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" />Invoices</Link>
      <PageHeader title={`${inv.number}${(inv.version || 1) > 1 ? ` v${inv.version}` : ""}`} subtitle={`${inv.customer_name} · issued ${fdate(inv.created_at)} · due ${fdate(inv.due_date)}`}>
        <RaiseTicketButton recordType="invoice" recordId={inv.id} label={inv.number} />
        <StatusBadge status={inv.status} testId="invoice-status-badge" className="self-center" />
        {inv.locked && <StatusBadge status="locked" testId="invoice-locked-badge" className="self-center" />}
        <Button variant="outline" data-testid="invoice-pdf-button" onClick={() => downloadWith(api, `/tenant/invoices/${inv.id}/pdf`, `${inv.number}.pdf`, true)}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
        {!cancelled && <SendDocButton kind="invoice" id={inv.id} />}
        {!cancelled && canWrite(["tenant_admin", "accountant"]) && <CancelInvoiceButton inv={inv} onDone={reload} />}
        {!cancelled && inv.status !== "paid" && canWrite(["tenant_admin", "accountant"]) && (
          <Button data-testid="record-payment-button" onClick={() => setPay(true)} className="bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white"><Wallet className="mr-1 h-4 w-4" />Record payment</Button>
        )}
        {!cancelled && inv.locked && canWrite(["tenant_admin", "accountant"]) && (
          <Button variant="outline" data-testid="invoice-unlock-button" onClick={() => setUnlock(true)}><Unlock className="mr-1 h-4 w-4" />Unlock</Button>
        )}
        <JobOrderPanel inv={inv} canCreate={canWrite(["tenant_admin", "accountant", "sales", "production"])} onDone={reload} />
        {!cancelled && d.quote && canWrite(["tenant_admin", "accountant", "sales"]) && <UpdateFromQuoteButton inv={inv} quote={d.quote} onDone={reload} />}
        {!cancelled && canWrite(["tenant_admin", "accountant", "sales"]) && (
          <Button data-testid="invoice-edit-button" disabled={inv.locked} onClick={() => setEdit(true)} className="bg-[#13294B] hover:bg-[#1c3a66]">
            {inv.locked ? <Lock className="mr-1 h-4 w-4" /> : <Pencil className="mr-1 h-4 w-4" />}Edit
          </Button>
        )}
      </PageHeader>
      {cancelled && <CancelledBanner inv={inv} />}
      {!cancelled && claim?.status === "pending" && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border px-5 py-3 text-sm po-tone-warning" data-testid="payment-claim-banner">
          <span className="flex-1"><b>Payment claimed, awaiting confirmation.</b> {claim.claimed_by} says they paid {fmt(claim.amount, inv.currency)} by {claim.method.replace("_", " ")}{claim.reference && ` (ref ${claim.reference})`} on {fdatetime(claim.claimed_at)}. This does not mark the invoice paid.</span>
          {canWrite(["tenant_admin", "accountant"]) && <><Button size="sm" data-testid="claim-confirm-button" onClick={() => resolveClaim("confirm")} className="bg-emerald-600 hover:bg-emerald-700">Confirm</Button>
            <Button size="sm" variant="outline" data-testid="claim-dismiss-button" onClick={() => resolveClaim("dismiss")}>Dismiss</Button></>}
        </div>
      )}
      {claim && claim.status !== "pending" && <p className="text-xs text-slate-500" data-testid="payment-claim-resolved">Last payment claim {claim.status} by {claim.resolved_by} on {fdatetime(claim.resolved_at)}.</p>}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard testId="invoice-total" tone="info" label="Invoice total" value={fmt(inv.total, inv.currency)} />
        <StatCard testId="invoice-paid" label="Amount paid" value={fmt(inv.amount_paid, inv.currency)} tone="success" />
        <StatCard testId="invoice-balance" tone={bal > 0.004 ? "warning" : "success"} label={inv.amount_credited ? `Balance (after ${fmt(inv.amount_credited, inv.currency)} credit)` : "Balance"} value={fmt(bal, inv.currency)} />
      </div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Section title="Line items">
            <div className="overflow-x-auto"><table className="po-table">
              <thead><tr><th>Description</th><th className="po-num">Qty</th><th className="po-num">Unit price</th><th>Taxes</th><th className="po-num">Line total</th></tr></thead>
              <tbody>{inv.line_items.map((l, i) => (
                <tr key={i}><td>{l.description}</td><td className="po-num">{l.quantity}</td><td className="po-num">{fmt(l.unit_price, inv.currency)}</td><td className="text-slate-500">{inv.apply_tax === false ? "Tax not applied" : (l.tax_codes || []).join(", ") || l.tax || "—"}</td><td className="po-num">{fmt(l.line_total, inv.currency)}</td></tr>
              ))}</tbody>
            </table></div>
            {inv.tax_breakdown && <div className="border-t border-slate-200 p-5"><TaxTotals calc={{ subtotal: inv.subtotal, breakdown: inv.tax_breakdown, total: inv.total }} currency={inv.currency} fmt={fmt} /></div>}
          </Section>
          {d.rules.schedule && <Section title="Milestone schedule"><MilestoneSchedule schedule={d.rules.schedule} currency={inv.currency} /></Section>}
          <Section title="Receipts">
            <div className="overflow-x-auto"><table className="po-table" data-testid="invoice-receipts-table">
              <thead><tr><th>Receipt</th><th>Date</th><th>Type</th><th>Method</th><th>Reference</th><th>Recorded by</th><th className="po-num">Amount</th></tr></thead>
              <tbody>
                {d.receipts.length === 0 && <EmptyRow cols={7} text="No receipts yet." />}
                {d.receipts.map((r) => (
                  <tr key={r.id} data-testid={`receipt-row-${r.number}`}><td className="font-mono text-xs">{r.number}</td><td>{fdate(r.date || r.created_at)}</td><td><StatusBadge status={r.kind === "reversal" || r.reversed ? "reversed" : "paid"} /></td><td>{r.method_label || r.method}</td><td className="text-xs">{r.reference || "—"}{r.note && <div className="text-slate-400">{r.note}</div>}</td><td>{r.recorded_by || "—"}</td><td className="po-num">{fmt(r.amount, r.currency)}</td></tr>
                ))}
                {d.credit_notes.map((c) => <tr key={c.id}><td className="font-mono text-xs">{c.number}</td><td>{fdate(c.date)}</td><td><StatusBadge status="comped" label="credit note" /></td><td>—</td><td className="text-xs">{c.reason}</td><td>{c.created_by}</td><td className="po-num">{fmt(c.amount, inv.currency)}</td></tr>)}
              </tbody>
            </table></div>
          </Section>
        </div>
        <div className="min-w-0 space-y-6">
          <Section title="Payment schedule">
            <dl className="space-y-2 px-5 py-4 text-sm">
              <div className="flex justify-between"><dt className="text-slate-500">Rule</dt><dd className="font-medium capitalize" data-testid="invoice-pay-rule">{d.rules.rule.replace("_", " ")}</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Outstanding</dt><dd className="po-num">{fmt(d.rules.outstanding, inv.currency)}</dd></div>
            </dl>
          </Section>
          <InvoiceVersions inv={inv} versions={d.versions} />
          <Section title="Audit trail"><AuditTrail rows={d.audit} /></Section>
        </div>
      </div>
      <InvoiceForm open={edit} onOpenChange={setEdit} invoice={inv} currency={inv.currency} onSaved={reload} />
      <RecordPaymentDialog inv={inv} outstanding={bal} open={pay} onOpenChange={setPay} onSaved={reload} />
      <Dialog open={unlock} onOpenChange={setUnlock}>
        <DialogContent data-testid="unlock-dialog">
          <DialogHeader><DialogTitle>Unlock {inv.number}</DialogTitle>
            <DialogDescription>This invoice has recorded payments. Unlocking allows one edit; the change and your reason are written to the audit trail. The new total cannot be less than the amount already paid.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="unlock-reason-input" placeholder="Reason for change (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter><Button data-testid="unlock-confirm-button" disabled={reason.trim().length < 3} onClick={doUnlock}>Unlock invoice</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
